import { z } from "zod";
import { SceneColorRole } from "./color-registry.js";
const Speed = z.number().min(0).max(10);
const Seed = z.number().int().min(0).max(65535);
const Corner = z.enum(["top-left", "top-right", "bottom-left", "bottom-right"]);
const Layer = z.discriminatedUnion("type", [
    z.strictObject({
        type: z.literal("horizon"),
        bands: z.number().int().min(1).max(3),
        divisions: z.number().int().min(4).max(18),
        coverage: z.number().min(0.01).max(0.25),
        speed: Speed,
        seed: Seed,
        color: SceneColorRole,
        shape: z.enum(["stepped", "smooth"]),
    }),
    z.strictObject({
        type: z.literal("frame"),
        shape: z.enum(["facade", "fragments"]),
        count: z.number().int().min(1).max(2),
        divisions: z.number().int().min(1).max(12),
        coverage: z.number().min(0.01).max(0.16),
        sideWidth: z.number().min(0.01).max(0.08),
        orientation: z.enum(["straight", "rising", "falling"]),
        speed: Speed,
        color: SceneColorRole,
    }),
    z.strictObject({
        type: z.literal("ground"),
        coverage: z.number().min(0.08).max(0.12),
        joints: z.number().int().min(0).max(8),
        opacity: z.number().min(0.05).max(0.15),
        speed: Speed,
        color: SceneColorRole,
    }),
    z.strictObject({
        type: z.literal("disc"),
        corner: Corner,
        radius: z.number().min(0.35).max(0.7),
        coverage: z.number().min(0.01).max(0.18),
        bands: z.number().int().min(1).max(2),
        speed: Speed,
        color: SceneColorRole,
        edge: SceneColorRole,
    }),
    z.strictObject({
        type: z.literal("stars"),
        count: z.number().int().min(1).max(100),
        radius: z.number().min(0.5).max(1.25),
        opacity: z.number().min(0.1).max(0.4),
        speed: Speed,
        seed: Seed,
        color: SceneColorRole,
    }),
    z.strictObject({
        type: z.literal("lights"),
        count: z.number().int().min(1).max(100),
        radius: z.number().min(1).max(2),
        opacity: z.number().min(0.1).max(0.4),
        speed: Speed,
        seed: Seed,
        color: SceneColorRole,
    }),
]);
export const Scenery = z
    .strictObject({
    profile: z.enum(["standard", "inherited-v1"]),
    skies: z
        .array(z.strictObject({
        stops: z
            .array(z.strictObject({ at: z.number().min(0).max(1), color: SceneColorRole }))
            .min(2)
            .max(4),
    }))
        .min(2)
        .max(3),
    layers: z.array(Layer).max(3),
    plane: z.strictObject({
        rays: z.union([
            z.literal(false),
            z.strictObject({ count: z.number().int().min(4).max(64), speed: z.number().min(0).max(0.1) }),
        ]),
        clouds: z.boolean(),
    }),
    rocket: z.strictObject({
        terrain: z.boolean(),
        clouds: z.boolean(),
        curve: z.boolean(),
        smoke: z.boolean(),
    }),
    debris: z.strictObject({
        enabled: z.boolean(),
        count: z.number().int().min(1).max(48),
        duration: z.number().min(50).max(1100),
        travel: z.number().min(0).max(240),
    }),
    trail: z.strictObject({
        style: z.enum(["none", "ribbon", "segments", "points"]),
        length: z.number().min(24).max(120),
        width: z.number().min(1).max(5.76),
        duration: z.number().min(100).max(600),
        opacity: z.number().min(0.1).max(0.5),
        count: z.number().int().min(1).max(48),
    }),
    curve: z.strictObject({ width: z.number().min(3).max(5), fillOpacity: z.number().min(0).max(0.55) }),
    axes: z.strictObject({ style: z.enum(["points", "ticks", "angles"]), radius: z.number().min(2).max(4) }),
    grid: z.strictObject({
        style: z.enum(["none", "solid", "dashed"]),
        horizontal: z.number().int().min(0).max(3),
        vertical: z.number().int().min(0).max(3),
        opacity: z.number().min(0.05).max(0.15),
    }),
    chrome: z.enum(["matte", "inset", "double-line"]),
    outline: z.strictObject({ enabled: z.boolean(), width: z.number().min(1.5).max(2) }),
})
    .superRefine((value, ctx) => {
    const issue = (message) => ctx.addIssue({ code: "custom", message });
    const normal = value.profile === "standard";
    if (normal) {
        if (value.rocket.smoke)
            ctx.addIssue({
                code: "custom",
                path: ["rocket", "smoke"],
                message: "legacy smoke is unavailable in standard scenery; set smoke to false and configure the bounded trail instead",
            });
        const bound = (actual, maximum, path) => {
            if (actual > maximum)
                ctx.addIssue({
                    code: "custom",
                    path,
                    message: `normal maximum ${maximum}; received ${actual} (excess ${actual - maximum})`,
                });
        };
        for (const [key, maximum] of Object.entries({
            length: 80,
            width: 2,
            duration: 400,
            opacity: 0.35,
            count: 8,
        }))
            bound(value.trail[key], maximum, ["trail", key]);
        bound(value.curve.fillOpacity, 0.08, ["curve", "fillOpacity"]);
        for (const [key, maximum] of Object.entries({ count: 8, duration: 250, travel: 24 }))
            bound(value.debris[key], maximum, ["debris", key]);
    }
    const planeHelpers = Number(value.plane.clouds) + Number(Boolean(value.plane.rays));
    const rocketHelpers = Number(value.rocket.clouds) + Number(value.rocket.terrain);
    const helperCoverage = normal
        ? Math.max(planeHelpers * 0.08, Number(value.rocket.clouds) * 0.08 + Number(value.rocket.terrain) * 0.1)
        : 0;
    const occupation = helperCoverage +
        value.layers.reduce((sum, layer) => sum + ("coverage" in layer ? layer.coverage : 0), 0);
    if (occupation > 0.3 + Number.EPSILON)
        issue("combined occupation must not exceed 30% of the scene");
    if (normal && value.layers.length + Math.max(planeHelpers, rocketHelpers) > 3)
        issue("optional clouds, terrain and rays count toward the three decoration groups");
    const populations = value.layers.filter((layer) => layer.type === "stars" || layer.type === "lights");
    if (populations.length > 2)
        issue("at most two light populations");
    if (populations.reduce((sum, layer) => sum + layer.count, 0) > 100)
        issue("combined population must not exceed 100 lights");
    // Bound illuminated area at the smallest supported plotting viewport; rendering also clips smaller hosts.
    if (populations.reduce((sum, layer) => sum + layer.count * (2 * layer.radius) ** 2, 0) > 320 * 240 * 0.01)
        issue("combined illuminated area must not exceed 1% at 320 x 240 CSS pixels");
    if (value.layers.filter((layer) => layer.type === "disc").length > 1)
        issue("at most one cropped disc");
    for (const sky of value.skies) {
        if (sky.stops[0]?.at !== 0 ||
            sky.stops.at(-1)?.at !== 1 ||
            sky.stops.some((stop, index) => index > 0 && stop.at <= sky.stops[index - 1].at))
            issue("sky stops must strictly increase from 0 to 1");
    }
});
/** The inherited profile is deliberately absent from creation defaults. */
export function standardScenery() {
    return {
        profile: "standard",
        skies: [
            {
                stops: [
                    { at: 0, color: "skyBaseTop" },
                    { at: 1, color: "skyBaseBottom" },
                ],
            },
            {
                stops: [
                    { at: 0, color: "skyUpperTop" },
                    { at: 1, color: "skyUpperBottom" },
                ],
            },
        ],
        layers: [],
        plane: { rays: false, clouds: false },
        rocket: { terrain: false, clouds: false, curve: false, smoke: false },
        debris: { enabled: false, count: 8, duration: 250, travel: 24 },
        trail: { style: "none", length: 48, width: 1, duration: 200, opacity: 0.2, count: 4 },
        curve: { width: 4, fillOpacity: 0.06 },
        axes: { style: "points", radius: 2.5 },
        grid: { style: "none", horizontal: 0, vertical: 0, opacity: 0.1 },
        chrome: "matte",
        outline: { enabled: false, width: 1.75 },
    };
}
//# sourceMappingURL=scenery.js.map